import React, { ReactNode, useMemo, useState } from "react";

import { useTranslation } from "utils";

import { CROP_ROTATION, CropMixComposerGoalResponse, CropsFilterState, SEED_COST_RATE } from "models";

import { FilterChip, FilterChipOption, Flex } from "components";

import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { getCostSymbolText } from "pages/app/crop-mix-composer/utils/cropMixComposer";

type CropsFilter = "family" | "cost";

export const CropsFilters = ({
  cropMixComposerResponse,
  filters,
  setFilters,
}: {
  cropMixComposerResponse?: CropMixComposerGoalResponse[];
  filters: CropsFilterState;
  setFilters: React.Dispatch<React.SetStateAction<CropsFilterState>>;
}) => {
  const { t } = useTranslation("cropMixComposer");

  const [openFilter, setOpenFilter] = useState<CropsFilter | null>(null);

  const availableCropFamilies = useMemo(
    () => Object.values(CROP_ROTATION).filter((cropFamily) => cropMixComposerResponse?.some((crop) => crop.crop.cropFamily === cropFamily)),
    [cropMixComposerResponse]
  );
  const availableCostCategories = useMemo(
    () => Object.values(SEED_COST_RATE).filter((costCategory) => cropMixComposerResponse?.some((crop) => crop.crop.coastCategory === costCategory)),
    [cropMixComposerResponse]
  );

  const toggleFilterValue = <Filter extends keyof CropsFilterState>(filter: Filter, value: CropsFilterState[Filter][number]) =>
    setFilters((prev) => {
      // indexing a generic key widens the element type, so the selected values need naming once
      const selectedValues = prev[filter] as CropsFilterState[Filter][number][];

      return {
        ...prev,
        [filter]: selectedValues.includes(value) ? selectedValues.filter((selected) => selected !== value) : [...selectedValues, value],
      };
    });

  const filterOptions = (options: { value: string; label: ReactNode; selected: boolean; onClick: () => void }[], dataTestId: string) => (
    <Flex vertical gap="0.25rem" className="filter-chip-popover-content">
      {options.map((option) => (
        <FilterChipOption
          key={option.value}
          label={option.label}
          selected={option.selected}
          onClick={option.onClick}
          dataTestId={`${dataTestId}-option-${option.value}`}
        />
      ))}
    </Flex>
  );

  return (
    <Flex className="crops-filters" gap="0.5rem" align="center" wrap="wrap">
      <FilterChip
        label={t(`page.${CMC_ROUTES.COMPOSITION}.filters.family`)}
        dataTestId="crops-filter-family"
        count={filters.cropFamilies.length}
        isOpen={openFilter === "family"}
        onOpenChange={(open) => setOpenFilter(open ? "family" : null)}
        content={filterOptions(
          availableCropFamilies.map((cropFamily) => ({
            value: cropFamily,
            label: t(`plantFamilies.${cropFamily}`),
            selected: filters.cropFamilies.includes(cropFamily),
            onClick: () => toggleFilterValue("cropFamilies", cropFamily),
          })),
          "crops-filter-family"
        )}
      />
      <FilterChip
        label={t(`page.${CMC_ROUTES.COMPOSITION}.filters.cost`)}
        dataTestId="crops-filter-cost"
        count={filters.costCategories.length}
        isOpen={openFilter === "cost"}
        onOpenChange={(open) => setOpenFilter(open ? "cost" : null)}
        content={filterOptions(
          availableCostCategories.map((costCategory) => ({
            value: costCategory,
            label: getCostSymbolText(costCategory),
            selected: filters.costCategories.includes(costCategory),
            onClick: () => toggleFilterValue("costCategories", costCategory),
          })),
          "crops-filter-cost"
        )}
      />
    </Flex>
  );
};
